import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Megaphone, Link2, Code2, Plus, ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/data";
import { Reveal, SectionHeading } from "@/components/Reveal";

const ICONS = { megaphone: Megaphone, link: Link2, code: Code2 };

export default function Services() {
  const [open, setOpen] = useState("digital-marketing");

  return (
    <section id="services" data-testid="services-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading id="services" overline="Services / 03" title="What I do for your growth" />
        <div className="space-y-4">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            const isOpen = open === s.id;
            return (
              <Reveal key={s.id} delay={i * 0.08}>
                <div
                  data-testid={`card-service-${s.id}`}
                  className={`rounded-2xl border transition-colors duration-500 overflow-hidden ${
                    isOpen ? "border-[#D9A441]/40 bg-[#101F38] shadow-lg shadow-black/30" : "border-white/10 bg-[#101F38]/60 hover:border-white/25"
                  }`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? null : s.id)}
                    data-testid={`btn-service-toggle-${s.id}`}
                    className="w-full flex items-center gap-5 sm:gap-8 p-6 sm:p-8 text-left"
                  >
                    <span className="font-editorial italic text-2xl sm:text-3xl text-[#D9A441]/80">{s.num}</span>
                    <span className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center border ${isOpen ? "bg-[#D9A441]/15 border-[#D9A441]/40 text-[#D9A441]" : "bg-white/5 border-white/10 text-[#C8D3E0]"}`}>
                      <Icon size={20} />
                    </span>
                    <span className="flex-1 font-display text-xl sm:text-2xl font-semibold tracking-tight text-[#F3EFE6]">{s.title}</span>
                    <motion.span animate={{ rotate: isOpen ? 45 : 0 }} transition={{ duration: 0.3 }} className="text-[#9DAEC4]">
                      <Plus size={22} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <div className="px-6 sm:px-8 pb-8 sm:pl-[7.5rem]">
                          <p className="text-[#9DAEC4] text-base leading-relaxed max-w-2xl">{s.desc}</p>
                          <div className="mt-5 flex flex-wrap gap-2.5">
                            {s.points.map((p) => (
                              <span key={p} className="text-xs font-mono uppercase tracking-widest text-[#D9A441] border border-[#D9A441]/25 rounded-full px-3.5 py-1.5 bg-[#D9A441]/5">
                                {p}
                              </span>
                            ))}
                          </div>
                          <a
                            href="#contact"
                            data-testid={`link-service-enquire-${s.id}`}
                            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#D9A441] hover:text-[#E5B85C] transition-colors"
                          >
                            Enquire about this service <ArrowUpRight size={15} />
                          </a>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
